import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { AttachmentItemDto, EmailSendModeDto, EmailSummaryDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import {
  AppAttachmentWithoutContent,
  DeltaEmailBasicUpdate,
  EmailBodyContentType,
} from '@/models/BasicEmail.model.ts';
import { useEmailsHandlers } from '@/views/emails/util/useEmailsHandlers.util.ts';

type Req = {
  currentFolderId?: string;
  currentPage: number;
  newEmail: DeltaEmailBasicUpdate;
};

type Return = {
  addNewEmail: (req: Req) => Promise<void>;
};

export const useAddNewEmail = (): Return => {
  const { refetchFolders } = useEmailsHandlers();
  const queryClient = useQueryClient();

  return {
    addNewEmail: async req => {
      await refetchFolders();
      const { currentFolderId, newEmail } = req;
      if (newEmail.email.userFolderId !== currentFolderId) {
        console.debug('newEmail.userFolderId !== currentFolderId');
        return;
      }
      const email = mapDeltaToEmail(newEmail.email);
      if (!email) {
        console.debug('!email');
        return;
      }

      await queryClient.refetchQueries({
        queryKey: [QueryKeyEnum.EMAILS.ALL],
      });

      await queryClient.refetchQueries({
        queryKey: [QueryKeyEnum.DASHBOARD.EMAILS],
      });

      await queryClient.refetchQueries(
        {
          queryKey: [QueryKeyEnum.EMAILS.THREAD],
        },
        { throwOnError: false },
      );
    },
  };
};

const mapDeltaToEmail = (newEmail: DeltaEmailBasicUpdate['email']): EmailSummaryDto | null => {
  if (
    !newEmail.emailThreadId.value?.value ||
    (!newEmail.from.value?.value && !newEmail.isDraft.value)
  ) {
    return null;
  }
  return {
    attachments: (newEmail.attachments.value ?? []).map(mapAttachments),
    attachmentsCount: newEmail.attachmentsCount.value ?? 0,
    bcc: newEmail.cc.value?.map(it => it.value) ?? [],
    body: newEmail.body.value ?? '',
    bodyContentType: newEmail.bodyContentType.value ?? EmailBodyContentType.TEXT,
    bodyPreview: newEmail.bodyPreview.value ?? '',
    cc: newEmail.cc.value?.map(it => it.value) ?? [],
    date: newEmail.sentDateTime.value ?? dayjs().set('year', 1970).toString(),
    emailId: newEmail.externalEmailId.value,
    from: newEmail.from.value?.value ?? '',
    isDraft: newEmail.isDraft.value ?? false,
    isRead: newEmail.isRead.value ?? true,
    sendMode: newEmail.sendMode.value ?? EmailSendModeDto.New,
    subject: newEmail.subject.value ?? '',
    threadId: newEmail.emailThreadId.value?.value,
    to: newEmail.to.value?.map(it => it.value) ?? [],
  };
};

const mapAttachments = (attachment: AppAttachmentWithoutContent): AttachmentItemDto => {
  return {
    attachmentId: attachment.attachmentId,
    contentType: attachment.contentType,
    externalAttachmentId: attachment.externalAttachmentId.value,
    name: attachment.fileName,
  };
};
